/**
 * @author        Pavlo Androshchuk
 * @copyright     Copyright (c) 2022, Zumper
 */

import type { SignInUrl, SignUpUrl } from '@zcode-ui/account'
import { MenuGroup } from '@zcode-ui/shadz-ui'
import * as React from 'react'
import { TRACK_USER_MENU_ACTION } from '../../../../constants'
import { useUserMenuContext } from '../../../../module/context/UserMenuContext'
import type { OnNavLinkClickProps, SubmenuType } from '../../../../types'
import { UserMenuBtnRow, UserMenuLinkRow } from '../../SharedComponents'
import { NearbyRentals } from '../../sections/NearbyRentals'
import { PropertyManagement } from '../../sections/PropertyManagement'
import { RenterHub } from '../../sections/RenterHub'
import { StayLength } from '../../sections/StayLength'

export interface MainUserSubmenuProps {
  handleSetActiveSubmenu: (navLinkClickProps: OnNavLinkClickProps, submenu: SubmenuType) => void
  isMessage3?: boolean
  onLoginClick: () => void
  onLogoutClick?: () => void
  /** passed by for you, but not by the 'normal' header in NavbarContainer */
  onSignupClick?: () => void
  signInHref: SignInUrl
  signUpHref: SignUpUrl
  unreadCount?: number
}

export const MainUserSubmenu = (props: MainUserSubmenuProps) => {
  const { dispatchTrackUserMenuClick, isLoggedIn } = useUserMenuContext()
  const {
    handleSetActiveSubmenu,
    isMessage3,
    onLoginClick: loginDispatch,
    onLogoutClick: logoutDispatch,
    onSignupClick: signupDispatch,
    signInHref,
    signUpHref,
    unreadCount,
  } = props satisfies MainUserSubmenuProps

  const handleOnClick = React.useCallback(
    (linkClicked: string, callback?: () => void) => {
      dispatchTrackUserMenuClick(linkClicked)
      callback?.()
    },
    [dispatchTrackUserMenuClick]
  )

  const onLoginClick = () => handleOnClick(TRACK_USER_MENU_ACTION.LOG_IN, loginDispatch)
  const onSignupClick = () => handleOnClick(TRACK_USER_MENU_ACTION.SIGN_UP, signupDispatch)
  const onLogoutClick = () => handleOnClick(TRACK_USER_MENU_ACTION.LOG_OUT, logoutDispatch)

  return (
    <React.Fragment>
      <StayLength />
      <NearbyRentals handleSetActiveSubmenu={handleSetActiveSubmenu} />
      <RenterHub
        handleSetActiveSubmenu={handleSetActiveSubmenu}
        isMessage3={isMessage3}
        unreadCount={unreadCount}
      />
      <PropertyManagement />
      <MenuGroup>
        {!isLoggedIn &&
          (signInHref ? (
            <React.Fragment>
              <UserMenuLinkRow onClick={onLoginClick} to={signInHref}>
                Log in
              </UserMenuLinkRow>
              <UserMenuLinkRow onClick={onSignupClick} to={signUpHref}>
                Sign up
              </UserMenuLinkRow>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <UserMenuBtnRow closeOnSelect onClick={onLoginClick} withIcon={false}>
                Log in
              </UserMenuBtnRow>
              <UserMenuBtnRow closeOnSelect onClick={onSignupClick} withIcon={false}>
                Sign up
              </UserMenuBtnRow>
            </React.Fragment>
          ))}
        {isLoggedIn && (
          <form action="/zumper-account/ssout" method="post" onSubmit={onLogoutClick}>
            <UserMenuBtnRow closeOnSelect type="submit" withIcon={false}>
              Log out
            </UserMenuBtnRow>
          </form>
        )}
      </MenuGroup>
    </React.Fragment>
  )
}
